import { Box, Stack, styled } from "@mui/material";

export const CanvasContainer = styled(Box)(({ theme }) => ({
	background: "linear-gradient(180deg, #F8FBFC 0%, #F2F6F9 100%)",
	flexGrow: 1,
	minWidth: 0,
	minHeight: 0,
	height: "100%",
	overflow: "hidden",
	borderRadius: 0,
	border: "none",
	// ReactFlow's default <Controls /> floating toolbar — restyle to match
	// the rest of the design system. Pill-rounded, soft shadow, brand teal hover.
	"& .react-flow__controls": {
		background: "#FFFFFF",
		borderRadius: 999,
		padding: "4px",
		display: "flex",
		flexDirection: "row",
		gap: "2px",
		boxShadow: "0 6px 18px rgba(13, 31, 48, 0.10), 0 1px 4px rgba(13, 31, 48, 0.06)",
		border: "1px solid rgba(13, 31, 48, 0.06)",
		bottom: 16,
		left: 16,
	},
	"& .react-flow__controls-button": {
		background: "transparent",
		border: "none",
		borderRadius: 999,
		width: 32,
		height: 32,
		color: "rgba(13, 31, 48, 0.55)",
		transition: "background 0.18s, color 0.18s",
	},
	"& .react-flow__controls-button:hover": {
		background: "rgba(2, 213, 171, 0.10)",
		color: "#02D5AB",
	},
	"& .react-flow__controls-button svg": {
		fill: "currentColor",
		maxWidth: 14,
		maxHeight: 14,
	},
	// QA fix (E1/E2): arrows are now GRAY (was teal) with rounded corners, and
	// the marching-ant dash is kept but made SUBTLE — sparser dashes, thinner
	// stroke, and a slower march — so the animation reads as "in progress / not
	// permanent" without being distracting. Gray #7E848C holds ~3.4:1 on the
	// #F2F6F9 canvas, preserving WCAG 2.1 AA 1.4.11 non-text contrast.
	"@keyframes coappMarchingAnts": {
		from: { strokeDashoffset: 22 },
		to:   { strokeDashoffset: 0 },
	},
	"& .react-flow__edge-path": {
		// Solid teal — an objectBoundingBox gradient vanishes on zero-width
		// (straight vertical) edges, which made arrows invisible.
		stroke: "#02D5AB",
		strokeWidth: 2,
		strokeLinecap: "round",
		strokeLinejoin: "round",
		strokeDasharray: "3 7",
		animation: "coappMarchingAnts 1.4s linear infinite",
	},
	"& .react-flow__edge.selected .react-flow__edge-path, & .react-flow__edge:focus .react-flow__edge-path, & .react-flow__edge:focus-visible .react-flow__edge-path": {
		stroke: "#5B616A",
		strokeWidth: 2.5,
	},
	// Solid brand-teal arrowhead with a softly rounded tip. Uses the gradient's
	// primary color as a SOLID fill (not the `url(#coapp-grad-primary)` gradient)
	// — an objectBoundingBox gradient is dropped to gray by the browser on the
	// marker's tiny/degenerate bbox, which read as a gray arrowhead. A solid
	// color always paints green. Round joins/caps keep the point soft.
	"& .react-flow__arrowhead polyline, & .react-flow__arrowhead path": {
		stroke: "#02D5AB",
		strokeWidth: 2,
		strokeLinejoin: "round",
		strokeLinecap: "round",
		fill: "#02D5AB",
	},
	// Background dot pattern — slightly more visible to give the canvas character.
	"& .react-flow__background pattern circle": {
		fill: "rgba(2, 213, 171, 0.18)",
	},
}));

export const CanvasLayout = styled(Stack)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	height: "100%",
	gap: theme.spacing(2),
}));

export const CanvasViewport = styled(Box)(() => ({
	flexGrow: 1,
	minHeight: 0,
	height: "100%",
	"& .react-flow": {
		width: "100%",
		height: "100%",
	},
	/**
	 * Connection handles straddle the node's top/bottom edge — half above,
	 * half below — so the dot looks like it's pinned to the node border
	 * and overhangs slightly. translate(-50%, -50%) centers the handle's
	 * own midpoint on the border line for both top and bottom positions.
	 */
	// Top handles are the incoming ("in") connection TARGETS — every node uses a
	// raw <Handle type="target" position={Position.Top}>, which React Flow paints
	// as its default dark (#1a192b) dot. Hide that dot for all nodes: keep the
	// handle present + positioned (so edges still anchor to it) but make it fully
	// invisible. The visible interactive handles (teal dot, "+" button) are all
	// bottom/source handles, so they're untouched.
	"& .react-flow__handle-top": {
		transform: "translate(-50%, -50%)",
		background: "transparent",
		border: "none",
		boxShadow: "none",
	},
	"& .react-flow__handle-bottom": {
		transform: "translate(-50%, -50%)",
	},
}));
